// src/contexts/AuthContext.tsx
import { createContext, useContext, useEffect, useState, type ReactNode, type JSX } from 'react';
import { type User, type Session } from '@supabase/supabase-js';
import { supabase } from '../clients/supabaseClient';
import type { Profile } from '../types';

interface AuthContextType {
    session: Session | null;
    user: User | null;
    profile: Profile | null;
    signIn: (email: string, password: string) => Promise<void>;
    signOut: () => Promise<void>;
    loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
    const [session, setSession] = useState<Session | null>(null);
    const [user,    setUser]    = useState<User | null>(null);
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const fetchProfile = async (userId: string) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();
            if (error) return;
            setProfile(data);
        } catch { /* non-fatal */ }
    };

    // ── Session init + auth state listener ────────────────────────────────────

    useEffect(() => {
        let mounted = true;

        const initSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (!mounted) return;
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) void fetchProfile(session.user.id);
            setLoading(false);
        };

        void initSession();

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            if (session?.user) void fetchProfile(session.user.id);
            else setProfile(null);
        });

        return () => {
            mounted = false;
            subscription.unsubscribe();
        };
    }, []);

    // ── Session lifecycle (focus / visibility) ─────────────────────────────────

    useEffect(() => {
        const ensureFreshSession = async () => {
            try {
                const { data: { session } } = await supabase.auth.getSession();
                if (!session) {
                    const { error } = await supabase.auth.refreshSession();
                    if (error) console.warn('Session refresh failed:', error.message);
                }
            } catch (e) {
                console.warn('Error ensuring fresh session:', e);
            } finally {
                window.dispatchEvent(new CustomEvent('app:resume'));
            }
        };

        const onFocus = () => {
            try { supabase.auth.startAutoRefresh?.(); } catch { /* empty */ }
            void ensureFreshSession();
        };

        const onBlur = () => {
            try { supabase.auth.stopAutoRefresh?.(); } catch { /* empty */ }
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') void onFocus();
            else onBlur();
        };

        void ensureFreshSession();
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibilityChange);
            onBlur();
        };
    }, []);

    // ── Auth actions ───────────────────────────────────────────────────────────

    const signIn = async (email: string, password: string): Promise<void> => {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
    };

    const signOut = async (): Promise<void> => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
        setSession(null);
        setUser(null);
        setProfile(null);
    };

    return (
        <AuthContext.Provider value={{ session, user, profile, signIn, signOut, loading }}>
            {children}
        </AuthContext.Provider>
    );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}